import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';

import type { Role } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { endpoints } from '../api/endpoints';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Loading } from './common';
import { useRouteFocus } from './a11y';

export function RequireAuth({ children, role }: { children: ReactNode; role?: Role }) {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (role && !hasRole(role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function Layout() {
  const { t } = useTranslation();
  const { user, logout, hasRole, setUser } = useAuth();
  const main = useRef<HTMLElement>(null);
  useRouteFocus(main);

  const links: [string, string][] = [
    ['/', 'nav.dashboard'],
    ['/live', 'nav.live'],
    ['/assess', 'nav.assess'],
    ['/sessions', 'nav.sessions'],
    ['/scenarios', 'nav.scenarios'],
    ['/voice-profiles', 'nav.voiceProfiles'],
    ['/statistics', 'nav.statistics'],
    ['/compare', 'nav.compare'],
    ['/profile', 'nav.profile'],
    ['/api-docs', 'nav.apiDocs'],
  ];
  const adminLinks: [string, string][] = [
    ['/admin/users', 'nav.users'],
    ['/admin/audit', 'nav.audit'],
  ];

  return (
    <div className="shell">
      <a
        href="#main"
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          main.current?.focus();
        }}
      >
        {t('nav.skipToContent')}
      </a>
      <header className="topbar">
        <span className="brand">{t('app.name')}</span>
        <div className="topbar-right">
          <LanguageSwitcher
            onChange={(lang) => {
              if (user) {
                void endpoints.users
                  .updateMe({ preferences: { ...user.preferences, language: lang } })
                  .then(setUser)
                  .catch(() => undefined);
              }
            }}
          />
          {user && <span className="muted">{user.full_name || user.email}</span>}
          <button type="button" onClick={() => void logout()}>
            {t('auth.logout')}
          </button>
        </div>
      </header>
      <nav aria-label={t('nav.mainNav')} className="sidebar">
        <ul>
          {links.map(([to, key]) => (
            <li key={to}>
              <NavLink to={to} end={to === '/'}>
                {t(key)}
              </NavLink>
            </li>
          ))}
        </ul>
        {hasRole('admin') && (
          <>
            <h2 className="nav-heading">{t('nav.admin')}</h2>
            <ul>
              {adminLinks.map(([to, key]) => (
                <li key={to}>
                  <NavLink to={to}>{t(key)}</NavLink>
                </li>
              ))}
            </ul>
          </>
        )}
      </nav>
      <main id="main" ref={main} tabIndex={-1} className="content">
        <Outlet />
      </main>
    </div>
  );
}
